import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { useCart } from "@/context/CartContext";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.76, 0, 0.24, 1] },
  }),
};

const Line = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { setOpen } = useCart();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" className="relative noise-overlay overflow-hidden pt-32 md:pt-40 pb-16">
      {/* floating shapes */}
      <motion.div style={{ y: useTransform(scrollYProgress, [0, 1], [0, -80]) }}
        className="pointer-events-none absolute top-28 right-[5%] hidden md:block z-10">
        <motion.img
          src="https://customer-assets-lqy194kg.emergentagent.net/job_party-gear-rent/artifacts/0y8rzw48_Logo%20Fiestas%20Moderno%20Simple%20en%20Negro%20y%20Rosa.png"
          alt="Cornerstone Celebrations"
          initial={{ rotate: -8, scale: 0.9, opacity: 0 }}
          animate={{ rotate: [-8, -4, -8], scale: 1, opacity: 1 }}
          transition={{ rotate: { duration: 6, repeat: Infinity, ease: "easeInOut" }, scale: { duration: 0.8, delay: 0.4 }, opacity: { duration: 0.8, delay: 0.4 } }}
          className="h-36 w-36 lg:h-44 lg:w-44 rounded-3xl object-cover border border-black/10 hard-shadow"
        />
      </motion.div>
      <motion.div style={{ y: useTransform(scrollYProgress, [0, 1], [0, 120]) }}
        className="pointer-events-none absolute bottom-24 left-[4%] h-16 w-16 rounded-2xl rotate-12 bg-brand-blue hidden md:block" />

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="flex text-brand-coral">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
          </div>
          <span className="text-sm font-600 text-ink/60">Trusted by 2,000+ local celebrations</span>
        </div>

        <h1 className="font-display font-900 tracking-tighter leading-[0.85] text-ink text-5xl md:text-7xl lg:text-[8vw]">
          <Line i={0}>Rent the</Line>
          <Line i={1} className="text-brand-coral">whole party.</Line>
          <Line i={2}>Delivered.</Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-6 font-display font-700 italic text-lg md:text-2xl text-brand-blue tracking-tight"
          data-testid="hero-slogan"
        >
          The Foundation of Every Great Celebration.
        </motion.p>

        <div className="mt-8 grid md:grid-cols-[1.1fr_0.9fr] gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="max-w-md text-base md:text-lg text-ink/70 font-500"
          >
            Bouncy houses, tents, tables, chairs & more — book your date online, pay a
            simple deposit, and we handle setup & pickup. Serving your neighborhood parties.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.6 }}
            className="flex flex-wrap gap-3 md:justify-end"
          >
            <button
              onClick={() => document.querySelector("#inventory")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-brand-coral text-white px-7 py-4 font-700 hover:-translate-y-1 hard-shadow-sm transition-transform"
              data-testid="hero-browse-btn"
            >
              Browse & Book
            </button>
            <button
              onClick={() => document.querySelector("#call")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-white border-2 border-ink px-7 py-4 font-700 hover:-translate-y-1 transition-transform"
              data-testid="hero-call-btn"
            >
              Schedule a Call
            </button>
          </motion.div>
        </div>

        {/* Hero image with parallax */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="relative mt-12 md:mt-16 rounded-[2rem] overflow-hidden border border-black/10 h-[42vh] md:h-[62vh]"
        >
          <motion.img
            style={{ y, scale }}
            src="https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxoYXBweSUyMHBhcnR5JTIwY2VsZWJyYXRpb24lMjBwZW9wbGV8ZW58MHx8fHwxNzgzOTUyNzkxfDA&ixlib=rb-4.1.0&q=85"
            alt="People celebrating at a party"
            className="w-full h-[120%] object-cover"
          />
          <div className="absolute bottom-5 left-5 flex items-center gap-2 rounded-full bg-white/90 backdrop-blur px-4 py-2 text-sm font-700">
            <ArrowDown size={16} className="text-brand-coral" /> Same-day setup available
          </div>
        </motion.div>
      </div>
    </section>
  );
}
